import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthFetch } from './useAuthFetch';

const preferencesBaseUrl = () =>
  `${process.env.REACT_APP_BACKEND_BASEURL}/api/user/preferences`;

/**
 * Load and persist a per-user preference via the backend API.
 * Optionally migrates from localStorage on first load when the server has no value.
 */
export function useUserPreference(preferenceKey, defaultValue, options = {}) {
  const { mergeWithDefault, localStorageKey } = options;
  const { authFetch, isReady } = useAuthFetch();
  const [value, setValueState] = useState(defaultValue);
  const [isLoaded, setIsLoaded] = useState(false);
  const valueRef = useRef(defaultValue);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const normalize = useCallback(
    (raw) => (mergeWithDefault ? mergeWithDefault(raw) : raw),
    [mergeWithDefault]
  );

  useEffect(() => {
    if (!isReady) return undefined;

    let cancelled = false;

    const load = async () => {
      try {
        const response = await authFetch(
          `${preferencesBaseUrl()}/${encodeURIComponent(preferenceKey)}`
        );
        if (cancelled) return;

        if (response.ok) {
          const json = await response.json();
          const serverValue = json?.data?.value;

          if (serverValue != null) {
            const merged = normalize(serverValue);
            setValueState(merged);
            if (localStorageKey) {
              localStorage.setItem(localStorageKey, JSON.stringify(merged));
            }
            setIsLoaded(true);
            return;
          }
        }

        if (localStorageKey) {
          const saved = localStorage.getItem(localStorageKey);
          if (saved) {
            try {
              const parsed = JSON.parse(saved);
              const merged = normalize(parsed);
              setValueState(merged);
              setIsLoaded(true);

              await authFetch(
                `${preferencesBaseUrl()}/${encodeURIComponent(preferenceKey)}`,
                {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ value: merged }),
                }
              );
              return;
            } catch (parseError) {
              console.error('Failed to parse local preference:', parseError);
            }
          }
        }

        setValueState(defaultValue);
        setIsLoaded(true);
      } catch (error) {
        console.error('Failed to load user preference:', error);
        if (cancelled) return;

        if (localStorageKey) {
          const saved = localStorage.getItem(localStorageKey);
          if (saved) {
            try {
              const parsed = JSON.parse(saved);
              setValueState(normalize(parsed));
              setIsLoaded(true);
              return;
            } catch (_) {
              // fall through to default
            }
          }
        }

        setValueState(defaultValue);
        setIsLoaded(true);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isReady, preferenceKey, authFetch, defaultValue, localStorageKey, normalize]);

  const setValue = useCallback((updater) => {
    setValueState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      valueRef.current = next;
      return next;
    });
  }, []);

  const save = useCallback(
    async (valueToSave) => {
      if (!isReady) return;

      const payload = valueToSave !== undefined ? valueToSave : valueRef.current;

      try {
        const response = await authFetch(
          `${preferencesBaseUrl()}/${encodeURIComponent(preferenceKey)}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ value: payload }),
          }
        );

        if (!response.ok) {
          throw new Error(`Save failed with status ${response.status}`);
        }

        if (localStorageKey) {
          localStorage.setItem(localStorageKey, JSON.stringify(payload));
        }
      } catch (error) {
        console.error('Failed to save user preference:', error);
      }
    },
    [authFetch, isReady, preferenceKey, localStorageKey]
  );

  const remove = useCallback(async () => {
    if (!isReady) return;

    try {
      await authFetch(
        `${preferencesBaseUrl()}/${encodeURIComponent(preferenceKey)}`,
        { method: 'DELETE' }
      );

      if (localStorageKey) {
        localStorage.removeItem(localStorageKey);
      }

      setValueState(defaultValue);
      valueRef.current = defaultValue;
    } catch (error) {
      console.error('Failed to delete user preference:', error);
    }
  }, [authFetch, isReady, preferenceKey, localStorageKey, defaultValue]);

  return { value: value, setValue, save, remove, isLoaded };
}
